import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Plus, X } from 'lucide-react';

const DuplicateContactDialog = ({ 
  open, 
  onClose, 
  existingContact, 
  newContact, 
  onReplace, 
  onAddNew, 
  onIgnore 
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <AlertTriangle className="w-6 h-6" />
            Duplicate Contact Detected
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Warning Message */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-800">
              A contact with similar information already exists. How would you like to proceed?
            </p>
          </div>

          {/* Comparison Table */}
          <div className="grid grid-cols-2 gap-4">
            {/* Existing Contact */}
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <h3 className="font-semibold text-blue-900 text-sm mb-2 flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  Existing Contact
                </h3>
              </div>
              
              <div className="space-y-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Name</p>
                  <p className="font-semibold text-gray-900">{existingContact?.name || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Designation</p>
                  <p className="text-gray-900">{existingContact?.title || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Company</p>
                  <p className="text-gray-900">{existingContact?.company || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <p className="text-gray-900 text-sm break-all">{existingContact?.email || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Phone</p>
                  <p className="text-gray-900">{existingContact?.phone || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* New Contact */}
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <h3 className="font-semibold text-green-900 text-sm mb-2 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  New Scanned Contact
                </h3>
              </div>
              
              <div className="space-y-2 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Name</p>
                  <p className="font-semibold text-gray-900">{newContact?.name || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Designation</p>
                  <p className="text-gray-900">{newContact?.title || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Company</p>
                  <p className="text-gray-900">{newContact?.company || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <p className="text-gray-900 text-sm break-all">{newContact?.email || 'N/A'}</p>
                </div>
                
                <div>
                  <p className="text-xs text-gray-500 uppercase">Phone</p>
                  <p className="text-gray-900">{newContact?.phone || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={onReplace}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-base"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Replace Existing Contact
              <span className="ml-auto text-xs bg-blue-500 px-2 py-1 rounded">Recommended</span>
            </Button>

            <Button
              onClick={onAddNew}
              variant="outline"
              className="w-full border-2 border-green-600 text-green-700 hover:bg-green-50 font-semibold py-6 text-base"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add as New Contact
            </Button>

            <Button
              onClick={onIgnore}
              variant="outline"
              className="w-full border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold py-6 text-base"
            >
              <X className="w-5 h-5 mr-2" />
              Ignore This Card
            </Button>
          </div>

          {/* Help Text */}
          <div className="text-xs text-gray-500 text-center space-y-1">
            <p><strong>Replace:</strong> Update the existing contact with new information</p>
            <p><strong>Add New:</strong> Keep both contacts (if they're actually different people)</p>
            <p><strong>Ignore:</strong> Discard the new card and keep the existing one</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateContactDialog;
